import { useEffect, useRef, useState } from "react";
import { Modal } from "./Modal";
import { Button } from "../Button";

interface OtpModalProps {
    open: boolean;
    inline?: boolean;
    length?: number;
    title?: string;
    description?: string;
    showTimer?: boolean;
    timerValue?: number;
    errorMessage?: string;
    onClose?: () => void;
    onSubmit: (otp: string) => Promise<void> | void;
    onResend: () => void;
}

export function OtpModal({
    open,
    inline = false,
    length = 4,
    title = "Enter OTP",
    description = `Enter the ${length}-digit code sent to you`,
    showTimer = true,
    timerValue = 59,
    errorMessage,
    onClose,
    onSubmit,
    onResend,
}: OtpModalProps) {
    const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
    const [timeLeft, setTimeLeft] = useState(timerValue);
    const [error, setError] = useState("");
    const [blink, setBlink] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

    useEffect(() => {
        if (!open) return;

        setOtp(Array(length).fill(""));
        setTimeLeft(timerValue);
        setError("");
        setBlink(false);
        setSubmitting(false);

        setTimeout(() => inputsRef.current[0]?.focus(), 0);
    }, [open, length, timerValue]);

    useEffect(() => {
        if (!open || !showTimer) return;

        const interval = setInterval(() => {
            setTimeLeft((t) => (t > 0 ? t - 1 : 0));
        }, 1000);

        return () => clearInterval(interval);
    }, [open, showTimer]);

    useEffect(() => {
        if (timeLeft > 10 || timeLeft === 0) {
            setBlink(false);
            return;
        }

        const blinkInterval = setInterval(() => {
            setBlink((b) => !b);
        }, 500);

        return () => clearInterval(blinkInterval);
    }, [timeLeft]);

    useEffect(() => {
        if (timeLeft === 0) {
            setError("OTP expired. Please request a new code.");
        }
    }, [timeLeft]);

    if (!open) return null;

    const submitOtp = async (code: string) => {
        if (submitting || timeLeft === 0) return;

        try {
            setSubmitting(true);
            setError("");
            await onSubmit(code);
        } catch {
            setOtp(Array(length).fill(""));
            setError("Invalid code. Try again.");
            inputsRef.current[0]?.focus();
        } finally {
            setSubmitting(false);
        }
    };

    const handleChange = (value: string, index: number) => {
        if (!/^\d?$/.test(value) || timeLeft === 0) return;

        const newOtp = [...otp];
        newOtp[index] = value;

        setOtp(newOtp);
        setError("");

        if (value && index < length - 1) {
            inputsRef.current[index + 1]?.focus();
        }

        if (value && index === length - 1 && !newOtp.includes("")) {
            submitOtp(newOtp.join(""));
        }
    };

    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>,
        index: number
    ) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (timeLeft === 0) return;

        e.preventDefault();

        const pasted = e.clipboardData.getData("text").slice(0, length);
        if (!/^\d+$/.test(pasted)) return;

        const newOtp = pasted.split("");
        const filled = [...newOtp, ...Array(length - newOtp.length).fill("")];

        setOtp(filled);

        if (!filled.includes("")) {
            submitOtp(filled.join(""));
        }
    };

    const handleResend = () => {
        setOtp(Array(length).fill(""));
        setTimeLeft(timerValue);
        setError("");
        inputsRef.current[0]?.focus();
        onResend();
    };

    const content = (
        <div className="p-4">

            {!inline && <h3 className="text-lg text-[#111111] font-semibold mb-3">{title}</h3>}
            {!inline && <p className="text-sm text-[#111111] mb-3">{description}</p>}

            <div className="flex justify-center gap-3">
                {otp.map((digit, index) => (
                    <input
                        key={index}
                        ref={(el) => { if (el) inputsRef.current[index] = el; }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        disabled={timeLeft === 0 || submitting}
                        onChange={(e) => handleChange(e.target.value, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onPaste={handlePaste}
                        className={`h-12 w-12 text-center text-xl font-semibold ${inline ? "text-white bg-black" : "  bg-white"} text-black border rounded-md
                        ${timeLeft <= 10 ? "border-red-500" : "border-[#333333]"}
                        focus:outline-none focus:ring-2 focus:ring-black`}
                        style={
                            timeLeft <= 10
                                ? { borderColor: blink ? "red" : "#333333" }
                                : undefined
                        }
                    />
                ))}
            </div>

            {showTimer && !error && timeLeft > 0 && (
                <p className={`text-center mb-4 mt-5 ${inline ? "text-white" : "text-black"}`}>
                    00:{String(timeLeft).padStart(2, "0")}
                </p>
            )}

            {errorMessage && (
                <p className="text-center mt-4 text-sm text-red-600">
                    {errorMessage}
                </p>
            )}

            {error && (
                <div className="text-center mt-4 space-y-2">
                    <p className="text-sm text-orange-600">{error}</p>
                    <Button
                        onClick={handleResend}
                        className="w-full rounded-lg bg-black py-3 font-semibold text-white"
                    >
                        Resend
                    </Button>
                </div>
            )}
        </div>
    );

    return inline ? content : <Modal isOpen={open} onClose={onClose}>{content}</Modal>;
}
